import type { AnyActorRef } from 'xstate';
import { assign, fromPromise, sendTo, setup } from 'xstate';
import type { ServiceError } from '~/features/alerts/errors';
import { createErrorAssigner, handleServiceError } from '~/features/alerts/errors';
import * as api from './api';
import { BypassApprovalData } from './api';
import { WelcomeValues } from './types';

/**
 * Provider Registration Service Machine
 *
 * PURE BUSINESS LOGIC & DATA MACHINE
 *
 * Responsibilities:
 * - API calls (fetch categories/types, update provider, upload CV, create role)
 * - Business rule validation
 * - Data persistence
 * - Registration completion flow
 *
 * States are business logic states, NOT UI states:
 * - idle: No active operation
 * - fetchingCategories: Loading categories
 * - fetchingTypes: Loading types for a category
 * - updatingProvider: Saving provider info
 * - uploadingCV: Uploading CV file
 * - creatingUserRole: Creating user role
 * - completed: Registration finished successfully
 *
 * Note: UI state (routes, page navigation) is handled by the VIEW machine.
 * This machine only cares about business data and operations.
 */

// Custom error messages for registration service
const registrationErrorMap = {
  400: { code: 'VALIDATION_ERROR', message: 'Invalid registration data' },
  401: { code: 'UNAUTHORIZED', message: 'Please log in to complete registration' },
  403: { code: 'FORBIDDEN', message: 'You do not have permission to register' },
  404: { code: 'NOT_FOUND', message: 'Registration resource not found' },
  409: { code: 'CONFLICT', message: 'Provider already exists' },
  413: { code: 'FILE_TOO_LARGE', message: 'CV file is too large' },
  415: { code: 'INVALID_FILE_TYPE', message: 'Invalid CV file type' },
  500: { code: 'SERVER_ERROR', message: 'Server error occurred during registration' },
};

export const employerRegistrationService = setup({
  types: {
    context: {} as {
      // Business data
      employerUserId: number | null;
      employerId: number | null;
      userInfoId: number;
      loading: boolean;
      error: ServiceError | null;
      registered: boolean;
      // Reference to parent actor for sending events
      parentRef: AnyActorRef;
    },
    events: {} as
      | { type: 'CREATE_EMPLOYER'; data: { welcome: WelcomeValues } }
      | { type: 'CREATE_USER_ROLE' }
      | { type: 'BYPASS_APPROVAL'; data: BypassApprovalData }
      | { type: 'SET_WELCOME_FORM'; data: WelcomeValues }
      | { type: 'SET_ERROR'; error: ServiceError },
    input: {} as {
      userInfoId: number;
      parentRef: AnyActorRef;
    },
  },
  actors: {
    createEmployer: fromPromise(
      async ({
        input,
      }: {
        input: {
          userInfoId: number;
        };
      }) => {
        return await api.createEmployer(input);
      }
    ),
    createUserRole: fromPromise(async ({ input }: { input: { userInfoId: number } }) => {
      return await api.createUserRole(input.userInfoId);
    }),
    bypassApproval: fromPromise(async ({ input }: { input: BypassApprovalData }) => {
      const data = input as BypassApprovalData;
      return await api.bypassApprovalAndCreateEmployer(data);
    }),
  },
  actions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    handleServiceError: handleServiceError as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    assignRegistrationError: createErrorAssigner(registrationErrorMap) as any,
  },
}).createMachine({
  id: 'providerRegistrationService',
  initial: '$_IDLE',
  context: ({ input }) => ({
    loading: false,
    employerUserId: null,
    employerId: null,
    userInfoId: input.userInfoId,
    registered: false,
    error: null,
    parentRef: input.parentRef,
  }),
  output: ({ context }) => {
    if (context.error) {
      return {
        status: 'error' as const,
        error: context.error,
        code: context.error.code || 'REGISTRATION_ERROR',
      };
    }
    return {
      status: 'success' as const,
      employerUserId: context.employerUserId ?? 0,
      employerId: context.employerId ?? 0,
      registered: context.registered,
      userInfoId: context.userInfoId,
      roleCode: 'EMPLOYER',
    };
  },
  states: {
    $_IDLE: {
      entry: [assign({ error: null })],
      on: {
        CREATE_EMPLOYER: {
          target: '$_CREATING_EMPLOYER',
        },
        CREATE_USER_ROLE: {
          target: '$_CREATING_USER_ROLE',
        },
        BYPASS_APPROVAL: {
          target: '$_BYPASSING_APPROVAL',
        },
      },
    },
    $_CREATING_EMPLOYER: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'createEmployer',
        input: ({ event, context }) => {
          const data = (event as { type: string; data: { welcome: WelcomeValues } }).data;
          return {
            userInfoId: context.userInfoId,
            welcome: data.welcome,
          };
        },
        onDone: {
          target: '$_IDLE',
          actions: [
            assign({
              employerUserId: ({ event }) => event.output.id ?? null,
              employerId: ({ event }) => event.output.employerId ?? null,
              loading: () => false,
            }),
            // Send event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_PROVIDER_CREATED',
                providerId: event.output.id,
              })
            ),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => {
              console.error(
                '[SERVICE] $_CREATING_PROVIDER: createProvider onError, error:',
                event.error
              );
            },
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
            // Send error event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_SERVICE_ERROR',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState errors can be any type
                error: event.error as any,
              })
            ),
          ],
        },
      },
    },
    $_CREATING_USER_ROLE: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'createUserRole',
        input: ({ context }) => {
          return { userInfoId: context.userInfoId };
        },
        onDone: {
          target: '$_COMPLETED',
          actions: [
            assign({
              registered: () => true,
              loading: () => false,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => {
              console.error(
                '[SERVICE] $_CREATING_USER_ROLE: createUserRole onError, error:',
                event.error
              );
            },
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
            // Send error event to parent view machine
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_SERVICE_ERROR',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState errors can be any type
                error: event.error as any,
              })
            ),
          ],
        },
      },
    },
    $_BYPASSING_APPROVAL: {
      entry: [assign({ loading: true, error: null })],
      invoke: {
        src: 'bypassApproval',
        input: ({ event }) => (event as { type: string; data: BypassApprovalData }).data,
        onDone: {
          target: '$_COMPLETED',
          actions: [
            assign({
              employerUserId: ({ event }) => event.output.employerUserId ?? null,
              employerId: ({ event }) => event.output.employerId ?? null,
              registered: () => true,
              loading: () => false,
            }),
          ],
        },
        onError: {
          target: '$_IDLE',
          actions: [
            ({ event }) => {
              console.error(
                '[SERVICE] $_BYPASSING_APPROVAL: bypassApproval onError, error:',
                event.error
              );
            },
            'handleServiceError',
            'assignRegistrationError',
            assign({ loading: () => false }),
            sendTo(
              ({ context }) => context.parentRef,
              ({ event }) => ({
                type: 'ON_SERVICE_ERROR',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState errors can be any type
                error: event.error as any,
              })
            ),
          ],
        },
      },
    },
    $_COMPLETED: {
      entry: [
        // Notify parent that registration is complete
        sendTo(({ context }) => context.parentRef, {
          type: 'ON_REGISTRATION_COMPLETE',
        }),
      ],
      output: ({ context }) => {
        return {
          status: 'success' as const,
          employerUserId: context.employerUserId ?? 0,
          employerId: context.employerId ?? 0,
          registered: true,
          userInfoId: context.userInfoId,
          roleCode: 'EMPLOYER',
        };
      },
      type: 'final' as const,
    },
  },
});
